import { DisplayObjectContainer, combineWithParent } from './DisplayObjectContainer';
import { DisplayObject, MousePick, PICK_MISS, PICK_PROPAGATE } from './DisplayObject';
import { Graphics } from './Graphics';
import type { Rectangle } from '../geom/Rectangle';
import { rt } from '../player/runtime';
import { TRect, invalidRect } from '../utils/numeric';

/** flash.display.Sprite. */
export class Sprite extends DisplayObjectContainer {
  /** @internal */ $graphics: Graphics | null;
  /** @internal */ $buttonMode: boolean;
  /** @internal */ $useHandCursor: boolean;
  /** @internal */ $hitArea: Sprite | null;
  /** @internal */ $dropTarget: DisplayObject | null;
  /** @internal */ $soundTransform: any;

  constructor() {
    super();
    this.$graphics = null;
    this.$buttonMode = false;
    this.$useHandCursor = true;
    this.$hitArea = null;
    this.$dropTarget = null;
    this.$soundTransform = null;
  }

  get graphics(): Graphics {
    return this.$graphics || (this.$graphics = new Graphics(this));
  }
  get buttonMode(): boolean {
    return this.$buttonMode;
  }
  set buttonMode(v: boolean) {
    this.$buttonMode = !!v;
  }
  get useHandCursor(): boolean {
    return this.$useHandCursor;
  }
  set useHandCursor(v: boolean) {
    this.$useHandCursor = !!v;
  }
  get hitArea(): Sprite | null {
    return this.$hitArea;
  }
  set hitArea(v: Sprite | null) {
    if (this.$hitArea) (this.$hitArea as any).$hitAreaOwner = null;
    this.$hitArea = v ?? null;
    if (v) (v as any).$hitAreaOwner = this;
  }
  get dropTarget(): DisplayObject | null {
    return this.$dropTarget;
  }
  get soundTransform(): any {
    return this.$soundTransform;
  }
  set soundTransform(v: any) {
    this.$soundTransform = v;
  }

  startDrag(lockCenter: boolean = false, bounds?: Rectangle): void {
    const p = rt().player;
    if (p) p.$startDrag(this, !!lockCenter, bounds ? bounds.clone() : null);
  }

  stopDrag(): void {
    const p = rt().player;
    if (p) p.$stopDrag(this);
  }

  $selfBounds(): TRect {
    return this.$graphics ? this.$graphics.$getBounds() : invalidRect();
  }

  $hitTestSelf(gx: number, gy: number): boolean {
    if (!this.$graphics) return false;
    const p = this.$globalToLocalPx(gx, gy);
    return !!p && this.$graphics.$hitTest(p[0], p[1]);
  }

  $mousePick(gx: number, gy: number): MousePick {
    const ha = this.$hitArea;
    if (ha) {
      // With a hitArea, the sprite is hit where its hit area is hit (children still take priority).
      const res = super.$mousePick(gx, gy);
      if (res.kind === 'hit' && res.target !== this) return res;
      if (!this.$visible) return PICK_MISS;
      if (ha.$hitTestShape(gx, gy, 0)) return this.$mouseEnabled ? { kind: 'hit', target: this } : PICK_PROPAGATE;
      return PICK_MISS;
    }
    return super.$mousePick(gx, gy);
  }
}

export { combineWithParent };
